/**
 * @file region-data.ts
 *
 * Builds the continent → country → state region tree the shipping forms use,
 * entirely from bundled static datasets — no API call, no backend endpoint. The
 * continent grouping + country list come from `countries-list` (whose continent
 * codes are exactly the WooCommerce set AF/AN/AS/EU/NA/OC/SA), and per-country
 * states (ISO 3166-2) from `country-region-data`. Both datasets are imported
 * dynamically so their JSON is code-split into a lazy chunk loaded only when a
 * region picker first opens.
 *
 * The English names produced here are fallbacks: {@link localizeContinents}
 * localizes continent and country names per locale via `Intl.DisplayNames`. State
 * names stay as the dataset's value (CLDR has no localized subdivision data).
 */

import type { ShopContinent } from '../api/shop-types';

type CountryState = ShopContinent['countries'][number]['states'][number];

/**
 * Loads and assembles the region tree from the bundled geographic datasets.
 *
 * @returns The continent tree: continents that have at least one country, sorted
 *   by name, each country's states sorted by name (regions without an ISO 3166-2
 *   code are dropped — they can't be addressed).
 */
export async function loadContinents(): Promise<ShopContinent[]> {
  const [{ continents, countries }, { allCountries }] = await Promise.all([
    import('countries-list'),
    import('country-region-data'),
  ]);

  const statesByCountry = new Map<string, CountryState[]>(
    allCountries.map(([, countryCode, regions]) => [
      countryCode,
      regions
        .filter(([, shortCode]) => shortCode)
        .map(([name, shortCode]) => ({ code: shortCode, name })),
    ]),
  );

  const countriesByContinent = new Map<string, ShopContinent['countries']>();
  for (const [code, country] of Object.entries(countries)) {
    const list = countriesByContinent.get(country.continent) ?? [];
    list.push({ code, name: country.name, states: statesByCountry.get(code) ?? [] });
    countriesByContinent.set(country.continent, list);
  }

  return Object.entries(continents)
    .map(([code, name]) => ({
      code,
      name,
      countries: (countriesByContinent.get(code) ?? []).sort((a, b) =>
        a.name.localeCompare(b.name),
      ),
    }))
    .filter((continent) => continent.countries.length > 0)
    .sort((a, b) => a.name.localeCompare(b.name));
}
